import { supabase } from './supabase';

type PartnerAdminAction =
  | { action: 'create_partner'; handle: string; displayName: string; email: string; tier: 'partner' | 'founding'; userId?: string }
  | { action: 'mint_code'; partnerId: string; code: string };

/**
 * Privileged browser calls terminate at the deployed staff gate. The caller's
 * JWT is forwarded by Supabase; the browser never receives a server credential.
 */
export async function invokePartnerAdmin(action: PartnerAdminAction): Promise<unknown> {
  const { data, error } = await supabase.functions.invoke('admin-partners', { body: action });
  if (error) throw error;
  return data;
}

export async function invokePayoutRoll(input: { periodStart: string; periodEnd: string; minCents?: number }): Promise<unknown> {
  const { data, error } = await supabase.functions.invoke('admin-payouts', { body: input });
  if (error) throw error;
  return data;
}

export type GovernanceAction =
  | { action: 'set_partner_status'; partnerId: string; status: string }
  | { action: 'assign_plan'; partnerId: string; planVersionId: string }
  | { action: 'approve_clawback'; eventId: string }
  | { action: 'reject_clawback'; eventId: string; reason: string };

/**
 * Privileged governance write (status, plan assignment, clawback decisions).
 * Terminates at the deployed staff gate; the operator's JWT is forwarded and the
 * browser never receives a server credential.
 */
export async function invokeGovernance(action: GovernanceAction): Promise<unknown> {
  const { data, error } = await supabase.functions.invoke('admin-governance', { body: action });
  if (error) throw error;
  return data;
}

export type OnboardingAdminAction =
  | { action: 'review_application'; applicationId: string; decision: 'approve' | 'reject'; reason: string; confirmed: true }
  | { action: 'issue_offer'; applicationId: string; planVersionId: string; personalizedSummary: string; expiresAt?: string; confirmed: true }
  | { action: 'set_onboarding_requirement'; partnerId: string; requirementKey: string; state: 'pending' | 'complete'; evidenceRef?: string; confirmed: true }
  | { action: 'transition_partner'; partnerId: string; toStatus: string; reason: string; confirmed: true }
  | { action: 'issue_referral_code'; partnerId: string; code: string; confirmed: true };

export async function invokeOnboardingAdmin(action: OnboardingAdminAction): Promise<unknown> {
  const { data, error } = await supabase.functions.invoke('admin-partner-onboarding', { body: action });
  if (error) throw error;
  return data;
}
